/**
 * The live pool of one protocol and one type's tickets with their replays. A type's tickets are read again while a
 * replay of one of them runs, and the pool while Promote writes the protocol's first types.
 */

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { promotionKey } from '@/hooks/use-promotion';
import { livePromotionService } from '@/services/live-promotion.service';
import type { LivePoolView, LiveTicket } from './types';

const REPLAY_POLL_MS = 5000;

export const liveKey = (...parts: string[]): string[] => promotionKey('live', ...parts);

const isReplaying = (tickets: LiveTicket[] | undefined): boolean =>
	Boolean(tickets?.some((ticket) => ticket.replay?.status === 'queued' || ticket.replay?.status === 'running'));

/** How often the pool is read while Promote writes the protocol's first scenario types. */
const TYPES_POLL_MS = 4000;

/** Types are being written: the pool changes when they land, with no other signal to wait for. */
const isWritingTypes = (view: LivePoolView | undefined): boolean =>
	view?.stage === 'not_prepared' && view.typeGeneration?.status === 'running';

const livePoolQueryAtom = (sopId: string) =>
	atomWithQuery(() => ({
		queryKey: liveKey(sopId),
		queryFn: () => livePromotionService.getLivePool(sopId),
		enabled: Boolean(sopId),
		staleTime: 0,
		retry: false,
		refetchInterval: (query: { state: { data?: LivePoolView } }) =>
			isWritingTypes(query.state.data) ? TYPES_POLL_MS : false
	}));

const typeTicketsQueryAtom = (sopId: string, typeId: string) =>
	atomWithQuery(() => ({
		queryKey: liveKey(sopId, 'type', typeId),
		queryFn: () => livePromotionService.getTypeTickets(sopId, typeId),
		enabled: Boolean(sopId && typeId),
		staleTime: 0,
		refetchInterval: (query) => (isReplaying(query.state.data) ? REPLAY_POLL_MS : false)
	}));

export function useLivePool(sopId: string) {
	const atom = useMemo(() => livePoolQueryAtom(sopId), [sopId]);
	const [query] = useAtom(atom);
	return query;
}

export function useTypeTickets(sopId: string, typeId: string) {
	const atom = useMemo(() => typeTicketsQueryAtom(sopId, typeId), [sopId, typeId]);
	const [query] = useAtom(atom);
	return query;
}
